import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Brain, ArrowRight, Wand2 } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "athletic_director" ? "athletic_director" : "coach";
  const initialMode = params.get("mode") === "login" ? "login" : "signup";

  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [programName, setProgramName] = useState("");
  const [programLevel, setProgramLevel] = useState("College");
  const [programSport, setProgramSport] = useState("Lacrosse");
  const [programGender, setProgramGender] = useState("Men");
  const [programLocation, setProgramLocation] = useState("");
  const [programSegment, setProgramSegment] = useState("college_varsity");
  const [programDivision, setProgramDivision] = useState("D1");
  const [busy, setBusy] = useState(false);
  const [isPlayer, setIsPlayer] = useState(params.get("player") === "1");
  const [playerLevel, setPlayerLevel] = useState("College");
  const [gradYear, setGradYear] = useState("");
  const [school, setSchool] = useState("");
  const [housingInterested, setHousingInterested] = useState(false);
  // Anti-bot honeypot — hidden from real users; bots that scrape and fill
  // every form field trip this and get rejected by the backend.
  const [hpWebsite, setHpWebsite] = useState("");
  // Optional bio URL for walk-in coach signups — after signup we run Claude
  // over this page to pre-fill the empty profile.
  const [bioUrl, setBioUrl] = useState("");

  const { login, signup } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let user;
      if (mode === "login") {
        user = await login(email, password);
      } else {
        const payload = { email, password, name, role, hp_website: hpWebsite };
        if (role === "coach" && isPlayer) {
          Object.assign(payload, {
            is_player: true,
            player_level: playerLevel,
            grad_year: gradYear ? Number(gradYear) : undefined,
            school: school || undefined,
            housing_interested: housingInterested,
          });
        }
        if (role === "athletic_director") {
          Object.assign(payload, {
            program_name: programName,
            program_level: programLevel,
            program_sport: programSport,
            program_gender: programGender,
            program_location: programLocation,
            program_segment: programSegment,
            program_division: programLevel === "College" ? programDivision : undefined,
          });
        }
        user = await signup(payload);
      }
      toast.success(`Welcome, ${user.name}.`);

      // Walk-in coach paste-bio-URL flow: after signup succeeds, if a bio URL
      // was provided, kick off Claude prefill and merge results into the fresh
      // (empty) coach_profile before navigating to /coach.
      if (mode === "signup" && role === "coach" && bioUrl.trim()) {
        try {
          toast.info("Reading your bio — one sec…", { duration: 4000 });
          const { data } = await api.post("/coaches/self-prefill-from-url", { url: bioUrl.trim() });
          const filled = data?.fields_filled?.length || 0;
          if (filled) toast.success(`Pre-filled ${filled} field${filled === 1 ? "" : "s"} from your bio — review + tweak on your dashboard.`, { duration: 6000 });
        } catch (err) {
          // Don't block the happy path — user is already logged in.
          const msg = err.response?.data?.detail || "Couldn't read that URL — you can paste it later from your dashboard.";
          toast.warning(msg, { duration: 6000 });
        }
      }

      const rawNext = params.get("next") || "";
      const safeNext = rawNext.startsWith("/") && !rawNext.startsWith("//") && !/^\/[a-z]+:/i.test(rawNext) ? rawNext : null;
      nav(safeNext || (user.is_platform_admin ? "/hq" : user.role === "coach" ? "/coach" : "/ad"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg text-white grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1623156884380-a080a56ddc93?fm=jpg&q=70&w=1600&auto=format&fit=crop')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-link">
            <Brain className="text-blaze" size={22} />
            <span className="heading text-2xl">CoachConnect <span className="text-blaze">AI</span></span>
          </Link>
          <div>
            <h1 className="heading text-5xl xl:text-6xl leading-none">CONNECT.<br />THE <span className="text-blaze">RIGHT</span> COACHES.<br />THE RIGHT PROGRAMS.</h1>
            <p className="text-neutral-400 mt-4 max-w-md">Lacrosse-first. Sport-agnostic at the graph layer. Seven AI agents working for you 24/7.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <Brain className="text-blaze" size={22} />
            <Link to="/" className="heading text-2xl">CoachConnect <span className="text-blaze">AI</span></Link>
          </div>

          <div className="flex gap-px bg-neutral-800 mb-6">
            {["signup", "login"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-3 heading text-lg tracking-wider uppercase transition-colors ${
                  mode === m ? "bg-blaze text-white" : "bg-appbg text-neutral-400 hover:text-white"
                }`}
              >
                {m === "signup" ? "Create" : "Sign in"}
              </button>
            ))}
          </div>

          <h2 className="heading text-4xl">{mode === "signup" ? (role === "coach" ? "STEP INTO THE ARENA" : "JOIN THE NETWORK") : "WELCOME BACK"}</h2>
          <p className="text-neutral-400 text-sm mt-2">
            {mode === "signup"
              ? (role === "coach"
                  ? "Free for coaches — always. Built for the ones actually in the arena — Roosevelt, 1910."
                  : "Free to sign up and post jobs. Program Recruiting is $5/month per program.")
              : "Pick up where you left off."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {/* Honeypot — hidden from users, visible to bots that scrape the form */}
            <div style={{ position: "absolute", left: "-9999px", top: 0, height: 0, width: 0, overflow: "hidden" }} aria-hidden="true">
              <label htmlFor="hp_website">Website (leave blank)</label>
              <input
                id="hp_website"
                name="hp_website"
                type="text"
                autoComplete="off"
                tabIndex={-1}
                value={hpWebsite}
                onChange={(e) => setHpWebsite(e.target.value)}
              />
            </div>
            {mode === "signup" && (
              <>
                <div className="flex gap-px bg-neutral-800">
                  {[
                    { k: "coach", label: "Coach — Seeking a Position" },
                    { k: "athletic_director", label: "HC / AD / Admin — Hiring" },
                  ].map((r) => (
                    <button
                      type="button"
                      key={r.k}
                      data-testid={`auth-role-${r.k}`}
                      onClick={() => setRole(r.k)}
                      className={`flex-1 py-3 text-sm uppercase tracking-widest transition-colors ${
                        role === r.k ? "bg-volt text-white" : "bg-appbg text-neutral-400 hover:text-white"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-neutral-500 leading-relaxed" data-testid="auth-role-explainer">
                  {role === "coach" ? (
                    <>
                      <span className="text-volt">Free for coaches — always.</span> Profile, matches, full program details and anonymous replies. No card, ever.
                    </>
                  ) : (
                    <>
                      <span className="text-volt">For Head Coaches, Athletic Directors, and administrators looking to fill a coaching position.</span>{" "}
                      Most assistant-coach searches are run by the Head Coach — sign up here to search the talent pool for your staff.
                      Free to sign up, post unlimited openings and manage applicants. Add{" "}
                      <span className="text-blaze">Program Recruiting — $5/month per program</span> for proactive talent search, matching and market intel. <Link to="/pricing" className="underline">See pricing</Link>.
                    </>
                  )}
                </p>
                <Field label="Full name">
                  <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="input" />
                </Field>
              </>
            )}
            <Field label="Email">
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input" />
            </Field>
            <Field label="Password">
              <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="input" minLength={6} />
            </Field>

            {mode === "signup" && role === "coach" && (
              <div className="border-t border-neutral-800 pt-4 space-y-2" data-testid="player-signup-section">
                <label className="flex items-center gap-2.5 text-sm text-neutral-300 cursor-pointer select-none bg-neutral-900/70 border border-neutral-800 px-3 py-2.5 hover:border-volt/60 transition-colors">
                  <input
                    data-testid="auth-player-checkbox"
                    type="checkbox"
                    checked={isPlayer}
                    onChange={(e) => setIsPlayer(e.target.checked)}
                    className="accent-[rgb(var(--app-primary-rgb))] h-4 w-4 shrink-0"
                  />
                  <span>I&apos;m a <b>current player</b> looking for coaching jobs (club, HS, college)</span>
                </label>
                {isPlayer && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select data-testid="auth-player-level" value={playerLevel} onChange={(e) => setPlayerLevel(e.target.value)} className="input">
                      <option value="HS">HS player</option>
                      <option value="College">College player</option>
                      <option value="Post-grad">Post-grad</option>
                    </select>
                    <input data-testid="auth-player-gradyear" type="number" min="2024" max="2035" placeholder="Grad year" value={gradYear} onChange={(e) => setGradYear(e.target.value)} className="input" />
                    <input data-testid="auth-player-school" placeholder="Your school" value={school} onChange={(e) => setSchool(e.target.value)} className="input" />
                  </div>
                )}
                {isPlayer && (
                  <label className="flex items-center gap-2.5 text-sm text-neutral-300 cursor-pointer select-none bg-neutral-900/70 border border-neutral-800 px-3 py-2.5 hover:border-volt/60 transition-colors">
                    <input
                      data-testid="auth-player-housing"
                      type="checkbox"
                      checked={housingInterested}
                      onChange={(e) => setHousingInterested(e.target.checked)}
                      className="accent-[rgb(var(--app-primary-rgb))] h-4 w-4 shrink-0"
                    />
                    <span>I&apos;d relocate for the season if the club provides housing</span>
                  </label>
                )}
              </div>
            )}

            {mode === "signup" && role === "coach" && (
              <div className="border-t border-neutral-800 pt-4 space-y-2">
                <div className="flex items-center gap-2 text-xs tracking-widest text-volt">
                  <Wand2 size={12} /> QUICK-START (OPTIONAL)
                </div>
                <label className="text-[11px] tracking-widest text-neutral-500">
                  Paste your athletic-dept bio, MaxPreps profile, or LinkedIn URL
                </label>
                <input
                  data-testid="auth-bio-url-input"
                  type="url"
                  value={bioUrl}
                  onChange={(e) => setBioUrl(e.target.value)}
                  placeholder="https://university.edu/athletics/coaches/your-bio"
                  className="input"
                />
                <p className="text-[11px] text-neutral-500 leading-relaxed">
                  We&apos;ll read the page with AI and pre-fill your coaching history, playing history, honors, and more.
                  Skip if you&apos;d rather fill in your profile by hand — you can always add a URL later from your dashboard.
                </p>
              </div>
            )}

            {mode === "signup" && role === "athletic_director" && (
              <div className="space-y-4 border-t border-neutral-800 pt-4">
                <div className="text-xs tracking-widest text-neutral-500">PROGRAM INFO</div>
                <Field label="I'm signing up as (I'm looking to FILL a coaching position)">
                  <select data-testid="auth-program-segment" value={programSegment} onChange={(e) => setProgramSegment(e.target.value)} className="input">
                    <option value="hs_ad">HS Athletic Director</option>
                    <option value="hs_head_coach">HS Head Coach — hiring my staff</option>
                    <option value="hs_parent_board">HS Parent Board / Booster</option>
                    <option value="college_club">College Club Director / Board</option>
                    <option value="college_head_coach">College Head Coach — hiring my staff</option>
                    <option value="college_varsity">NCAA Athletic Director</option>
                  </select>
                </Field>
                <Field label="Program / School / Club name">
                  <input data-testid="auth-program-name-input" value={programName} onChange={(e) => setProgramName(e.target.value)} required className="input" />
                </Field>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Sport">
                    <select data-testid="auth-program-sport" value={programSport} onChange={(e) => setProgramSport(e.target.value)} className="input">
                      {[
                        { v: "Lacrosse", on: true },
                        { v: "Football", on: false },
                        { v: "Basketball", on: false },
                        { v: "Baseball", on: false },
                        { v: "Soccer", on: false },
                        { v: "Volleyball", on: false },
                      ].map((s) => <option key={s.v} value={s.v} disabled={!s.on}>{s.v}{s.on ? "" : " — coming soon"}</option>)}
                    </select>
                  </Field>
                  <Field label="Gender">
                    <select data-testid="auth-program-gender" value={programGender} onChange={(e) => setProgramGender(e.target.value)} className="input">
                      {["Men", "Women", "Coed"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Level">
                    <select data-testid="auth-program-level" value={programLevel} onChange={(e) => setProgramLevel(e.target.value)} className="input">
                      {["MS", "HS", "JUCO", "College"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                {programLevel === "College" && (
                  <Field label="College Division (which level of college are you?)">
                    <select data-testid="auth-program-division" value={programDivision} onChange={(e) => setProgramDivision(e.target.value)} className="input">
                      {["D1", "D2", "D3", "NAIA", "NJCAA", "MCLA D1", "MCLA D2", "WCLA", "NCLA", "Club"].map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="Location (City, State)">
                  <input data-testid="auth-program-location" value={programLocation} onChange={(e) => setProgramLocation(e.target.value)} className="input" />
                </Field>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid={mode === "signup" ? "auth-submit-signup" : "auth-submit-signin"}
              className="w-full bg-blaze text-white heading text-xl py-4 tracking-wider hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? "WORKING…" : (mode === "signup" ? "CREATE ACCOUNT" : "LOG IN")} <ArrowRight size={18} />
            </button>
            {mode === "login" && (
              <Link
                to="/forgot-password"
                data-testid="auth-forgot-password-link"
                className="block text-center text-neutral-400 text-xs mt-3 hover:text-volt transition-colors"
              >
                Forgot your password?
              </Link>
            )}
          </form>

          <style>{`
            .input { width: 100%; background: #121212; border: 1px solid #27272A; padding: 12px 14px; color: white; outline: none; font-size: 14px; }
            .input:focus { border-color: #FF3B30; box-shadow: 0 0 0 1px #FF3B30; }
          `}</style>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] tracking-widest text-neutral-500 mb-1.5">{label.toUpperCase()}</div>
      {children}
    </label>
  );
}
